'use client'

import { useZoomImageMove } from '@zoom-image/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useLocale } from '@/components/LocaleContext'

import { ImageZoomModal, type ZoomImage } from './ImageZoomModal'
import { ProductImageMagnifier } from './ProductImageMagnifier'

const LG_BREAKPOINT = 1024
const TAP_MOVE_THRESHOLD = 10
const TAP_MAX_DURATION = 300

type ProductImageZoomProps = {
  src: string
  alt: string
  galleryImages: ZoomImage[]
  children?: React.ReactNode
}

export function ProductImageZoom({ src, alt, galleryImages, children }: ProductImageZoomProps) {
  const { locale } = useLocale()
  const tByLocale: Record<
    string,
    {
      pinchToZoom: string
      tapToEnlarge: string
    }
  > = {
    en: {
      pinchToZoom: 'Pinch or drag to zoom · Tap to enlarge',
      tapToEnlarge: 'Tap to enlarge image',
    },
    fr: {
      pinchToZoom: 'Pincez ou faites glisser pour zoomer · Appuyez pour agrandir',
      tapToEnlarge: "Appuyez pour agrandir l'image",
    },
    ar: {
      pinchToZoom: 'قرص أو اسحب للتكبير · اضغط للتكبير',
      tapToEnlarge: 'اضغط لتكبير الصورة',
    },
  }
  const t = tByLocale[locale] ?? tByLocale.en

  const mobileContainerRef = useRef<HTMLDivElement>(null)
  const { createZoomImage } = useZoomImageMove()
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIndex, setModalIndex] = useState(0)
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)
  const movedRef = useRef(false)
  const handledTouchRef = useRef(false)

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`)
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (isDesktop !== false || !mobileContainerRef.current) return
    createZoomImage(mobileContainerRef.current, {
      zoomFactor: 2.5,
      zoomImageSource: src,
      disabledContextMenu: true,
      zoomImageProps: { alt },
    })
  }, [isDesktop, src, alt, createZoomImage])

  const currentIndex = galleryImages.findIndex((img) => img.src === src)
  const resolvedIndex = currentIndex >= 0 ? currentIndex : 0

  const openModal = useCallback(() => {
    setModalIndex(resolvedIndex)
    setModalOpen(true)
  }, [resolvedIndex])

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      touchStartRef.current = null
      return
    }
    const touch = e.touches[0]
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() }
    movedRef.current = false
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || e.touches.length !== 1) {
      movedRef.current = true
      return
    }
    const touch = e.touches[0]
    const dx = Math.abs(touch.clientX - touchStartRef.current.x)
    const dy = Math.abs(touch.clientY - touchStartRef.current.y)
    if (dx > TAP_MOVE_THRESHOLD || dy > TAP_MOVE_THRESHOLD) {
      movedRef.current = true
    }
  }

  const handleTouchEnd = () => {
    if (!touchStartRef.current || movedRef.current) {
      touchStartRef.current = null
      return
    }
    const elapsed = Date.now() - touchStartRef.current.time
    if (elapsed <= TAP_MAX_DURATION) {
      handledTouchRef.current = true
      openModal()
    }
    touchStartRef.current = null
  }

  const handleClick = () => {
    if (handledTouchRef.current) {
      handledTouchRef.current = false
      return
    }
    openModal()
  }

  if (isDesktop === null) {
    return (
      <div className="relative aspect-square w-full max-w-lg overflow-hidden rounded-lg bg-neutral-50">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-contain object-center"
          draggable={false}
        />
        {children}
      </div>
    )
  }

  if (isDesktop) {
    return (
      <>
        <ProductImageMagnifier src={src} alt={alt}>
          {children}
        </ProductImageMagnifier>
        <ImageZoomModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          images={galleryImages}
          initialIndex={modalIndex}
        />
      </>
    )
  }

  return (
    <>
      <div className="relative flex-1 min-h-0 w-full max-w-lg overflow-visible">
        <div
          ref={mobileContainerRef}
          className="relative aspect-square w-full overflow-hidden rounded-lg bg-neutral-50 touch-none select-none"
          style={{ touchAction: 'none' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-label={t.tapToEnlarge}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openModal()
            }
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-contain object-center pointer-events-none"
            draggable={false}
          />
          {children}
        </div>
        <p className="mt-1 font-poppins text-xs text-neutral-500">{t.pinchToZoom}</p>
      </div>
      <ImageZoomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={galleryImages}
        initialIndex={modalIndex}
      />
    </>
  )
}
